/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/
import * as React from "react";
import { CallToAction, Plan, PlanTitle } from "components/Plans/Plan";
import iconCheck from "images/check-light.svg";
import styled from "styled-components";
import { webComponentShim } from "components/shared/WebComponentShim";
import { useCurrentUser } from "actions/CurrentUserActions";
import { PurchaseExpressButton } from "components/PurchaseExpress";
import { PremiumIcon } from "components/shared/PremiumIcon";
import { ExpressCheckoutButtonPosition } from "constants/Analytics";
import { ProductFeature } from "components/Plans/ProductFeature";
import {
  EnhancePremiumFeatureList,
  MicCheckFeatureList,
  StudioFeatureList,
  ProductList,
} from "components/Plans/ProductList";
import { PlanSubtitleContainer } from "components/Plans/Plan";
import "stylesheets/spectrum.scss";

const Button = styled(webComponentShim("sp-button")).attrs({
  size: "xl",
  static: "white",
})`
  padding-left: 10px;
`;

const Link = styled.a.attrs({
  className: "spectrum-Link",
})`
  color: white !important;
  margin-top: 16px;
  display: "block";
`;

export const PremiumPlan: React.FC = () => {
  const { currentUser } = useCurrentUser();

  return (
    <Plan data-testid={"plan-premium"} premium={true}>
      <PlanTitle />
      <PlanSubtitleContainer>
        {I18n.t("plans.premium.subtitle")}
      </PlanSubtitleContainer>
      <ProductList
        products={[
          EnhancePremiumFeatureList,
          StudioFeatureList,
          MicCheckFeatureList,
        ]}
      />
      {currentUser?.hasPremiumAccess ? (
        <CallToAction>
          <Button disabled>
            <PremiumIcon slot="icon" />
            {I18n.t("plans.premium.button_access")}
          </Button>
          <Link href={process.env.ACCOUNTS_PAGE}>
            {I18n.t("plans.premium.your_account")}
          </Link>
        </CallToAction>
      ) : (
        <CallToAction data-testid="call-to-action">
          <PurchaseExpressButton
            color={"white"}
            position={ExpressCheckoutButtonPosition.FEATURES_PREMIUM_PANEL}
            size={"xl"}
          >
            <PremiumIcon slot="icon" size={20} />
            {I18n.t("plans.premium.button")}
          </PurchaseExpressButton>
          <ProductFeature image={iconCheck}>
            {I18n.t("plans.premium.reminder")}
          </ProductFeature>
        </CallToAction>
      )}
    </Plan>
  );
};
